/* AdminPanel — single entry point that App.jsx imports.
   Owns the modal UI state (new license / assign licenses / invite user)
   and routes the active tab to the right page component. Data + actions
   come from the parent (App.jsx) so license/user/dmKey fetch lifecycles
   stay near the existing state until a follow-up commit hoists them. */
import React, { useState } from "react";
import AdminLayout from "./AdminLayout";
import Overview from "./pages/Overview";
import Licenses from "./pages/Licenses";
import Users from "./pages/Users";
import IntakeQueue from "./pages/IntakeQueue";
import DocMayaKeys from "./pages/DocMayaKeys";
import Revenue from "./pages/Revenue";
import ActivityLog from "./pages/ActivityLog";
import NewLicenseModal from "./modals/NewLicenseModal";
import InviteUserModal from "./modals/InviteUserModal";
import AssignLicensesModal from "./modals/AssignLicensesModal";
var E = React.createElement;

/* Map URL slugs (used in #/admin/<section>) to internal tab ids.
   AdminTabs already speaks slug; this module accepts both for resilience.
   PR-5.6: alias "activity-log" -> "activity" so external links keep
   working when the slug shorthand drifts. */
var SLUG_ALIASES = {
  "docmaya-keys": "docmaya-keys",
  "docmaya":      "docmaya-keys",
  "activity-log": "activity",
  "activity_log": "activity"
};
function normalizeTab(t){
  if(!t) return "overview";
  return SLUG_ALIASES[t] || t;
}

export default function AdminPanel(props){
  var active = normalizeTab(props.adminTab);
  var data = props.data || {};
  var actions = props.actions || {};
  var showToast = props.showToast || function(){};

  /* Modal state lives here so closing one tab doesn't lose unrelated state. */
  var newLic = useState(false); var newLicOpen = newLic[0], setNewLicOpen = newLic[1];
  var inv    = useState(false); var invOpen    = inv[0],    setInvOpen    = inv[1];
  var assign = useState(null);  var assignFor  = assign[0], setAssignFor  = assign[1];

  function onCreateLicense(payload){
    /* Pass the parent's create result back to the modal so it can
       show the GUID + key in its success view. The modal closes itself
       via the "Done" button in that view; we only close synchronously
       on outright failure (result === false). */
    return Promise.resolve(actions.createLicense && actions.createLicense(payload))
      .then(function(result){
        if(result === false) return false;
        return result;
      });
  }
  function onInvite(payload){
    Promise.resolve(actions.inviteUser && actions.inviteUser(payload))
      .then(function(){ setInvOpen(false); });
  }
  function onAssignSave(ids){
    if(!assignFor) return;
    actions.assignLicenses && actions.assignLicenses(assignFor, ids);
    setAssignFor(null);
  }

  function renderPanel(){
    switch(active){
      case "overview":
        return E(Overview, {
          licenses: data.licenses, users: data.users, activity: data.activity });
      case "licenses":
        return E(Licenses, {
          licenses: data.licenses, loading: data.licensesLoading, error: data.licensesError,
          onRefresh: actions.loadLicenses,
          onCreate: function(){ setNewLicOpen(true); },
          onSuspend: actions.suspendLicense,
          onActivate: actions.activateLicense,
          onDelete:   actions.deleteLicense,
          showToast: showToast });
      case "users":
        return E(Users, {
          users: data.users, loading: data.usersLoading, error: data.usersError,
          updatingId: data.usersUpdatingId,
          onRefresh: actions.loadUsers,
          onChangeTier: actions.changeUserTier,
          onToggleApproved: actions.toggleUserApproved,
          onAssignLicenses: function(u){ setAssignFor(u); },
          onInvite: function(){ setInvOpen(true); } });
      case "intake-queue":
        /* IntakeQueue is self-fetching (PR-K soft-preview shape) so no
           data/actions plumbing is needed here; the toast surface stays
           consistent with the other tabs. */
        return E(IntakeQueue, { showToast: showToast });
      case "docmaya-keys":
        return E(DocMayaKeys, {
          keys: data.dmKeys, loading: data.dmKeysLoading, error: data.dmKeysError,
          onRefresh: actions.loadDmKeys,
          onGenerate: actions.generateDmKey,
          onRevoke: actions.revokeDmKey,
          showToast: showToast });
      case "revenue":
        return E(Revenue, { licenses: data.licenses });
      case "activity":
        return E(ActivityLog, {
          entries: data.activity, loading: data.activityLoading,
          error: data.activityError, onRetry: actions.loadActivity });
      default:
        return E(Overview, {
          licenses: data.licenses, users: data.users, activity: data.activity });
    }
  }

  return E("div",null,
    E(AdminLayout, {
      active: active,
      onChange: function(slug){ props.setAdminTab && props.setAdminTab(slug); }
    }, renderPanel()),
    E(NewLicenseModal, {
      open: newLicOpen, onClose: function(){ setNewLicOpen(false); },
      onCreate: onCreateLicense, showToast: showToast }),
    E(InviteUserModal, {
      open: invOpen, onClose: function(){ setInvOpen(false); },
      onInvite: onInvite, showToast: showToast }),
    E(AssignLicensesModal, {
      open: !!assignFor, user: assignFor, licenses: data.licenses,
      onClose: function(){ setAssignFor(null); }, onSave: onAssignSave })
  );
}
